/**
 * The three fit numbers the try-on scene consumes. Size multiplies the
 * calibrated frame width; the offsets are added to the calibrated ones and are
 * expressed in frame-widths along the head's own axes.
 */

/** The frame as calibrated, with no correction applied. */
export const NEUTRAL_FIT = { sizeScale: 1, offsetY: 0, offsetZ: 0 };

/**
 * Proportions of MediaPipe's canonical face model — the mesh its landmark
 * regressor is fitted against, and so the closest thing to an "average face"
 * expressed in the exact landmark indices we measure at runtime.
 *
 * This is also, implicitly, the face the catalog was calibrated on: those
 * values were tuned until each frame looked right on *a* face, and auto-fit's
 * job is to carry that tuning across to faces shaped differently. A wearer
 * whose proportions match these gets no correction at all.
 */
export const REFERENCE_FACE = {
  eyeSpanRatio: 0.4112,
  eyeDropRatio: 0.0436,
  bridgeProtrusion: 0.7691,
};

/** Canonical nasion protrusion in face widths — the length scale depth uses. */
const REFERENCE_NASION_DEPTH = 0.4984;

/**
 * Eye span sets the frame size only halfway.
 *
 * Two rules compete: a frame should be about as wide as the face at the
 * temples, and its lens centres should sit over the pupils. Face width already
 * drives the base scale, so following eye span the whole way would overrule it
 * and leave wide-set eyes in frames that jut past the temples. Half of the
 * difference splits it and keeps both roughly satisfied.
 */
const SIZE_GAIN = 0.5;

/** Depth leans on MediaPipe's z, the least trustworthy axis, so it corrects gently. */
const DEPTH_GAIN = 0.6;

/**
 * Ceilings on how far a measurement may move each value.
 *
 * Auto-fit is a correction, not a replacement for calibration: a bad frame — a
 * profile shot, a face at the edge of the lens — must not be able to fling the
 * frame off the head. Clamping degrades the worst case to roughly the fixed
 * behaviour calibration alone would give.
 */
const LIMITS = {
  sizeScale: { min: 0.85, max: 1.15 },
  offsetY: { min: -0.09, max: 0.09 },
  offsetZ: { min: -0.06, max: 0.06 },
};

/**
 * Turns measured face proportions into the fit the wearer would otherwise have
 * had to dial in by hand.
 *
 * Each value is a *deviation* from the reference face, so the whole thing is a
 * no-op on an average face and the catalog's per-model calibration keeps
 * meaning what it always meant.
 *
 * @param {ReturnType<import("./face-metrics").createEmptyMetrics>} metrics
 * @param {number} widthRatio  The model's calibrated frame-width-over-face-width.
 */
export function deriveAutoFit(metrics, widthRatio) {
  const sizeScale = clamp(
    1 + SIZE_GAIN * (metrics.eyeSpanRatio / REFERENCE_FACE.eyeSpanRatio - 1),
    "sizeScale",
  );

  // Face widths -> frame widths. Floored so a degenerate calibration can't
  // divide a small deviation up into a huge offset.
  const frameWidth = Math.max(widthRatio * sizeScale, 0.1);

  // Eyes further below the bridge than average means the frame has to come
  // down to meet them, hence the sign flip.
  const eyeDropDeviation = metrics.eyeDropRatio - REFERENCE_FACE.eyeDropRatio;
  const offsetY = clamp(-eyeDropDeviation / frameWidth, "offsetY");

  // bridgeProtrusion is a pure shape ratio, so the deviation is a *percentage*
  // more or less protruding than average. Multiplying the canonical nasion
  // depth by it turns that back into a length without ever trusting the
  // absolute scale of MediaPipe's z.
  const depthDeviation =
    REFERENCE_NASION_DEPTH * (metrics.bridgeProtrusion / REFERENCE_FACE.bridgeProtrusion - 1);
  const offsetZ = clamp((DEPTH_GAIN * depthDeviation) / frameWidth, "offsetZ");

  return { sizeScale, offsetY, offsetZ };
}

function clamp(value, key) {
  if (!Number.isFinite(value)) return NEUTRAL_FIT[key];
  const { min, max } = LIMITS[key];
  return Math.min(max, Math.max(min, value));
}
